import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { Markdown } from "#/components/Markdown";
import { ActionAnchor, Container, SectionLabel, Tag } from "#/components/site";
import { getProjectBySlug, projects, siteMeta } from "#/content/portfolio";
import { buildSeoHead } from "#/lib/seo";

type ProjectLink = {
  href: string;
  label: string;
  variant: "primary" | "secondary";
};

export const Route = createFileRoute("/work/$slug")({
  component: WorkDetail,
  loader: ({ params }) => {
    const project = getProjectBySlug(params.slug);

    if (!project) {
      throw notFound();
    }

    return { project };
  },
  head: ({ params }) => {
    const project = getProjectBySlug(params.slug);

    const isNightWatch = project?.slug === "ukraine-night-watch";
    const title = isNightWatch
      ? "Night Watch Ukraine | Geospatial Replay with deck.gl and MapLibre"
      : `${project?.title ?? "Work"} | ${siteMeta.name}`;
    const description = isNightWatch
      ? "Alexander Horner’s Night Watch Ukraine case study covers 2D and 3D alert maps and a 31-day historical replay built with deck.gl, MapLibre, and Cloudflare Workers."
      : (project?.summary ?? "Portfolio case study");

    return buildSeoHead({
      title,
      description,
      path: project ? `/work/${project.slug}` : "/work",
      image: project
        ? {
            path: project.ogImage,
            type: "image/webp",
            width: 1200,
            height: 630,
          }
        : undefined,
      type: project ? "article" : "website",
    });
  },
});

function WorkDetail() {
  const { project } = Route.useLoaderData();
  const currentIndex = projects.findIndex(
    (entry) => entry.slug === project.slug,
  );
  const nextProject = projects[(currentIndex + 1) % projects.length];
  const galleryMidpoint = Math.ceil(project.gallery.length / 2);
  const galleryLeading = project.gallery.slice(0, Math.max(1, galleryMidpoint));
  const galleryTrailing = project.gallery.slice(Math.max(1, galleryMidpoint));
  const galleryFeature = project.testimonial
    ? {
        body: project.testimonial.quote,
        eyebrow: project.testimonial.attribution,
        isQuote: true,
      }
    : {
        body: project.note.value,
        eyebrow: project.note.label,
        isQuote: false,
      };
  const projectLinks: ProjectLink[] = [
    project.links?.live
      ? {
          href: project.links.live,
          label: "Visit project",
          variant: "secondary",
        }
      : null,
    project.links?.article
      ? {
          href: project.links.article,
          label: "View publication",
          variant: "secondary",
        }
      : null,
    project.links?.repository
      ? {
          href: project.links.repository,
          label: "View code",
          variant: "secondary",
        }
      : null,
  ].filter((value): value is ProjectLink => value !== null);

  return (
    <main id="main-content" className="pt-28">
      <section className="pb-16 md:pb-24">
        <Container>
          <div className="mb-12 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-5xl">
              <SectionLabel>
                Case study {String(project.order).padStart(2, "0")}
              </SectionLabel>
              <h1 className="mt-4 font-serif-display text-5xl leading-[1.04] tracking-[-0.04em] text-primary sm:text-7xl lg:text-[5.35rem]">
                {project.title}
              </h1>
            </div>
            <p className="text-[0.72rem] font-semibold tracking-[0.18em] text-muted uppercase">
              Timeline: {project.timeline}
            </p>
          </div>

          <div className="relative overflow-hidden bg-surface-container">
            <img
              src={project.heroImage}
              alt={project.heroImageAlt}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="aspect-21/9 w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-primary/5 to-primary/30" />
          </div>
        </Container>
      </section>

      <section className="bg-surface-alt py-20">
        <Container className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-7">
            <SectionLabel>The vision</SectionLabel>
            <Markdown html={project.html} />
          </div>

          <aside className="space-y-10 md:col-span-4 md:col-start-9">
            <DetailGroup label="Technologies">
              <div className="flex flex-wrap gap-2">
                {project.stack.map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </div>
            </DetailGroup>

            <DetailGroup label="Role">
              <p className="font-serif text-2xl italic text-primary">
                {project.role}
              </p>
            </DetailGroup>

            <DetailGroup label="Deliverables">
              <ul className="space-y-3 pl-0">
                {project.deliverables.map((item) => (
                  <li
                    key={item}
                    className="list-none text-base leading-7 text-muted"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </DetailGroup>

            {projectLinks.length > 0 ? (
              <DetailGroup
                label="Links"
                className="border-t border-outline/30 pt-6"
              >
                <div className="flex flex-col gap-3">
                  {projectLinks.map((link) => (
                    <ActionAnchor
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      variant={link.variant}
                      className="justify-between"
                    >
                      {link.label}
                      <ExternalLink className="size-4" aria-hidden="true" />
                    </ActionAnchor>
                  ))}
                </div>
              </DetailGroup>
            ) : null}
          </aside>
        </Container>
      </section>

      <section className="py-24 md:py-32">
        <Container>
          <div className="grid gap-8 md:grid-cols-3">
            <article className="bg-surface-raised p-10 md:col-span-2 md:p-12">
              <SectionLabel>Strategic goals</SectionLabel>
              <ol className="mt-8 space-y-8 pl-0">
                {project.goals.map((goal, index) => (
                  <li key={goal.title} className="flex list-none gap-4">
                    <span className="font-serif text-2xl italic text-secondary whitespace-nowrap">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h2 className="text-lg font-semibold text-primary">
                        {goal.title}
                      </h2>
                      <p className="mt-2 text-base leading-8 text-muted">
                        {goal.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </article>

            <article className="flex flex-col justify-between bg-primary-container p-10 text-white md:p-12">
              <div>
                <SectionLabel className="text-warm">The challenge</SectionLabel>
                <p className="mt-6 text-base leading-8 text-white/74">
                  {project.challenge.summary}
                </p>
              </div>
              <div className="mt-10">
                <SectionLabel className="text-warm">Resolution</SectionLabel>
                <p className="mt-3 text-sm leading-7 text-white/74 italic">
                  {project.challenge.resolution}
                </p>
              </div>
            </article>
          </div>
        </Container>
      </section>

      <section className="bg-surface-alt py-20">
        <Container>
          <div className="columns-1 md:columns-2 md:gap-x-8">
            {galleryLeading.map((image) => (
              <GalleryFigure key={image.src} src={image.src} alt={image.alt} />
            ))}

            <article className="mb-8 flex min-h-80 break-inside-avoid flex-col justify-center bg-primary p-12 text-center text-white">
              <h2
                className={`font-serif text-2xl ${galleryFeature.isQuote ? "italic" : ""}`}
              >
                {galleryFeature.isQuote
                  ? `"${galleryFeature.body}"`
                  : galleryFeature.body}
              </h2>
              <p className="mt-4 text-[0.72rem] font-semibold tracking-[0.18em] text-warm uppercase">
                {galleryFeature.eyebrow}
              </p>
            </article>

            {galleryTrailing.map((image) => (
              <GalleryFigure key={image.src} src={image.src} alt={image.alt} />
            ))}
          </div>
        </Container>
      </section>

      <section className="py-28 text-center md:py-40">
        <Container>
          <SectionLabel className="text-muted">Next Experience</SectionLabel>
          <Link
            to="/work/$slug"
            params={{ slug: nextProject.slug }}
            className="mt-6 inline-flex items-center gap-4 font-serif-display text-5xl tracking-[-0.03em] text-primary no-underline transition hover:text-secondary sm:text-7xl lg:text-[5.5rem]"
          >
            <span className="italic">{nextProject.title}</span>
            <ArrowRight className="size-8 shrink-0 text-secondary sm:size-10" />
          </Link>
        </Container>
      </section>
    </main>
  );
}

type DetailGroupProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

function DetailGroup({ label, children, className }: DetailGroupProps) {
  return (
    <section className={className}>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-4">{children}</div>
    </section>
  );
}

type GalleryFigureProps = {
  src: string;
  alt: string;
};

function GalleryFigure({ src, alt }: GalleryFigureProps) {
  return (
    <figure className="group mb-8 break-inside-avoid bg-surface-raised p-4">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="aspect-4/3 h-auto w-full object-cover transition duration-700 ease-out group-hover:scale-[1.02]"
      />
    </figure>
  );
}
